import {
  Card,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { StyledTextField } from "../../../components/StyledTextField";
import { StyledButton } from "../../../components/StyledButton";
import { ArrowLeftOutlined, Check } from "@mui/icons-material";
import { useFormik } from "formik";
import { routesConfig } from "../../../config/routes";
import { useQueryParams } from "../../../hooks/ContextualNavigations";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useIocContext } from "../../../contexts/ioc/IocContext";
import { Types } from "../../../ioc/types";
import { IAuthService } from "../../../modules/user/models/IAuthService";
import useDialogAlert from "../../../hooks/useDialogAlert";
import AppError from "../../../utils/appError";

const ForgotPasswordTrading = (): JSX.Element => {
  const [successSendEmail, setSuccessSendEmail] = useState(false);

  const { snackbar } = useDialogAlert();
  const { serviceContainer } = useIocContext();
  const { query, queryDict } = useQueryParams();

  const theme = useTheme();
  const navigate = useNavigate();

  const authService = serviceContainer.get<IAuthService>(
    Types.User.IAuthService
  );

  const changePassword = useCallback(
    async (values: any, resetForm: VoidFunction) => {
      try {
        const { payload, url, rules, path } = queryDict;
        const metadata = payload ? { payload } : { url, rules, path };

        await authService.forgetPassword({
          email: values.email,
          metadata,
        });

        resetForm();

        setSuccessSendEmail(true);

        snackbar({
          message: "Email Enviado com sucesso",
          variant: "success",
        });
      } catch (error) {
        setSuccessSendEmail(false);

        if (error instanceof AppError) {
          snackbar({ message: error.message, variant: error.variant });
        } else {
          snackbar({
            message: "Não foi possível enviar o email, tente novamente.",
            variant: "error",
          });
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [authService, queryDict]
  );
  const titleAndSubtitleMailCard = useMemo(
    () => ({
      title: successSendEmail ? "Confira seu e-mail" : "Digite seu e-mail",
      subtitle: successSendEmail
        ? "Enviamos instruções sobre uma nova senha para o seu e-mail"
        : "Enviaremos instruções para a redefinição da sua senha",
    }),
    [successSendEmail]
  );

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .required("Campo obrigatório")
      .email(
        "Insira um e-mail com formato válido. Exemplo: exemplo@hotmail.com"
      ),
  });

  const {
    errors,
    values,
    touched,
    isSubmitting,
    handleBlur,
    handleSubmit,
    setFieldValue,
    resetForm,
  } = useFormik({
    initialValues: { email: "" },
    validationSchema: validationSchema,
    onSubmit: (values) => changePassword(values, resetForm),
  });

  const verifyEmailToShowCheck = useMemo(
    () => touched.email && !errors.email,
    [touched.email, errors.email]
  );

  return (
    <Stack
      sx={{
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
      }}
    >
      <Card
        sx={{
          display: "flex",
          flexDirection: "column",
          padding: "2rem",
          rowGap: "2rem",
          maxWidth: "50rem",
          width: "95%",
        }}
      >
        <IconButton
          sx={{ alignSelf: "flex-start", position: "absolute" }}
          onClick={() => navigate(`${routesConfig.LOGIN}${query}`)}
        >
          <ArrowLeftOutlined />
        </IconButton>

        <img
          src="https://atem.com.br/wp-content/themes/conatus-atem/img/logo/Logo-Atem-PNG.png"
          alt="Grupo Atem"
          style={{
            width: "13rem",
            height: "7rem",
            objectFit: "fill",
            alignSelf: "center",
          }}
        />

        <Stack>
          <Typography
            sx={{
              fontSize: "2.5rem",
              fontWeight: "bold",
              fontFamily: "Poppins",
              textAlign: "center",
              color: theme.palette.grey[700],
            }}
          >
            {titleAndSubtitleMailCard.title}
          </Typography>

          <Typography
            sx={{
              fontFamily: "Poppins",
              textAlign: "center",
              color: theme.palette.grey[700],
            }}
          >
            {titleAndSubtitleMailCard.subtitle}
          </Typography>
        </Stack>

        {!successSendEmail && (
          <>
            <Divider sx={{ margin: "1rem -2rem" }} />

            <form
              style={{
                display: "flex",
                flexDirection: "column",
                rowGap: "2rem",
              }}
              onSubmit={handleSubmit}
            >
              <StyledTextField
                type="email"
                id="email"
                variant="standard"
                size="small"
                value={values.email}
                onChange={({ target: { value } }) => {
                  setFieldValue("email", value);
                }}
                onBlur={handleBlur("email")}
                fullWidth
                placeholder="Digite o email"
                labelprops={{
                  label: "E-mail",
                  hasTouched: touched.email,
                  hasErros: errors.email,
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton edge="end">
                        <Check
                          sx={{
                            color: verifyEmailToShowCheck
                              ? "#0BB873"
                              : "transparent",
                          }}
                        />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <StyledButton
                type="submit"
                variant="contained"
                fullWidth
                loading={isSubmitting}
                sx={{
                  backgroundColor: theme.palette.grey[700],
                  "&:hover": {
                    backgroundColor: theme.palette.grey[700],
                  },
                }}
              >
                Continuar
              </StyledButton>
            </form>
          </>
        )}
      </Card>
    </Stack>
  );
};

export default ForgotPasswordTrading;
